import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useWeekIndex } from "@/contexts/WeekIndexProvider";
import { useLastNameFirst } from "@/hooks/useNameFormat";
import { useRosterLookup } from "@/hooks/useRosterLookup";
import { useScheduleDays } from "@/hooks/useScheduleDays";
import { useScheduleCacheEnabled } from "@/hooks/useScheduleWeekRules";
import { useShownWeek } from "@/hooks/useShownWeek";
import { getWeekStart } from "@/utils/dateUtils";
import { DEFAULT_WEEK_START_DAY, type WeekData } from "@/utils/models";
import { panelWeeksOf } from "@/utils/panelWeeks";
import { toIsoDate } from "@/utils/scheduleWeeks";

const DAYS_PER_WEEK = 7;
const noRefetch = (): void => undefined;

export type ShiftInsightsWeek = {
	/** The weeks Shift Insights reads, in the old list's shape; the shown one is `weeks[weekIndex]`. */
	weeks: WeekData[];
	weekIndex: number;
	week: WeekData | undefined;
	isLoading: boolean;
	isError: boolean;
	refetch: () => void;
	hasPreviousWeek: boolean;
	hasNextWeek: boolean;
	showPreviousWeek: () => void;
	showNextWeek: () => void;
	/** Shows the week holding `date`. */
	showWeekOf: (date: DateTime) => void;
};

/**
 * The Sunday week Shift Insights shows. With `staffing_schedule_cache` off, the old list's week at the shared position,
 * paged within the loaded weeks, as before. On, the shown week built from the cache, paged by date between the
 * schedule's first and last day.
 */
export const useShiftInsightsWeek = (): ShiftInsightsWeek => {
	const isCacheEnabled = useScheduleCacheEnabled();
	const { hospitalData, getWeekIndexByDateTime, chosenDepartment } = useHospitalData();
	const { weekIndex, setWeekIndex } = useWeekIndex();
	const { weekStart, goToWeek } = useShownWeek();
	// DEFAULT: today's week until a page names one.
	const shownWeekIso = toIsoDate(getWeekStart(weekStart ?? DateTime.now(), DEFAULT_WEEK_START_DAY));
	const shownWeekStart = useMemo(() => DateTime.fromISO(shownWeekIso, { zone: chosenDepartment.timezone }), [shownWeekIso, chosenDepartment.timezone]);
	const shownWeekEnd = shownWeekStart.plus({ days: DAYS_PER_WEEK - 1 });
	const days = useScheduleDays(shownWeekStart, shownWeekEnd);
	const roster = useRosterLookup(chosenDepartment.department_id);
	const lastNameFirst = useLastNameFirst();
	const cachedWeeks = useMemo(
		() => (isCacheEnabled ? panelWeeksOf(days.days, roster.getNurseById, lastNameFirst) : []),
		[isCacheEnabled, days.days, roster.getNurseById, lastNameFirst],
	);
	const refetch = useCallback(() => {
		days.refetch();
		roster.refetch();
	}, [days.refetch, roster.refetch]);

	if (!isCacheEnabled) {
		return {
			weeks: hospitalData.weeks,
			weekIndex,
			week: hospitalData.weeks[weekIndex],
			isLoading: false,
			isError: false,
			refetch: noRefetch,
			hasPreviousWeek: weekIndex > 0,
			hasNextWeek: weekIndex < hospitalData.weeks.length - 1,
			showPreviousWeek: () => setWeekIndex(weekIndex - 1),
			showNextWeek: () => setWeekIndex(weekIndex + 1),
			showWeekOf: (date) => setWeekIndex(getWeekIndexByDateTime(date)),
		};
	}
	const { scheduleStartDate, scheduleEndDate } = days;
	return {
		weeks: cachedWeeks,
		weekIndex: 0,
		week: cachedWeeks[0],
		isLoading: days.isLoading || roster.isLoading,
		isError: days.isError || roster.isError,
		refetch,
		hasPreviousWeek: !!scheduleStartDate && toIsoDate(shownWeekStart) > toIsoDate(scheduleStartDate),
		hasNextWeek: !!scheduleEndDate && toIsoDate(shownWeekEnd) < toIsoDate(scheduleEndDate),
		showPreviousWeek: () => goToWeek(shownWeekStart.minus({ weeks: 1 })),
		showNextWeek: () => goToWeek(shownWeekStart.plus({ weeks: 1 })),
		showWeekOf: (date) => goToWeek(date),
	};
};
